// Import necessary dependencies for React hooks
import React, { useState, useEffect } from 'react';

// VehicleChecklistAddModal component for adding or editing vehicle checklist details in a modal
const VehicleChecklistAddModal = ({
  show, // Boolean to control modal visibility
  onClose, // Function to close the modal
  formData, // Object containing form input data
  onChange, // Function to handle form input changes
  onSave, // Function to save form data
  isEditMode, // Boolean to determine if modal is in edit mode
}) => {
  // State to track form validation status
  const [validated, setValidated] = useState(false);
  // State to track validation status of individual fields
  const [validatedFields, setValidatedFields] = useState({});
  // State to control visibility of subitem form
  const [showSubItemForm, setShowSubItemForm] = useState(false);

  // Show subitem form if child items exist
  useEffect(() => {
    if (formData.child && formData.child.length > 0) {
      setShowSubItemForm(true); // Display subitem form if child array is not empty
    }
  }, [formData.child]); // Dependency on child array

  // Handle form submission with validation
  const handleSubmit = (e) => {
    e.preventDefault();
    setValidated(true); // Mark form as validated

    const fieldsToValidate = ['name', 'criteria'];
    const touched = {};
    fieldsToValidate.forEach(field => {
      touched[field] = true; // Mark fields as touched
    });
    setValidatedFields(touched); // Update validated fields state

    // Check parent fields
    if (!formData.name || !formData.criteria) {
      return; // Exit if parent fields are invalid
    }

    // If subitems exist, validate them too
    if (formData.child && formData.child.length > 0) {
      const isAnySubitemInvalid = formData.child.some(
        (item) => !item.name?.trim() || !item.criteria?.toString().trim() // Validate subitem fields
      );

      if (isAnySubitemInvalid) {
        return; // Exit if any subitem is invalid
      }
    }

    // All validations passed
    onSave(); // Call save function
  };

  // Handle adding a new subitem
  const handleAddSubItem = () => {
    setShowSubItemForm(true); // Show subitem form
    if (!formData.child || formData.child.length === 0) {
      onChange({
        target: {
          name: 'child',
          value: [{
            name: '', // Initialize empty subitem name
            criteria: '', // Initialize empty subitem criteria
          }]
        }
      }); // Add new subitem to child array
    }
  };

  // Handle changes to subitem fields
  const handleSubItemChange = (index, e) => {
    const newChildItems = [...formData.child]; // Copy current child items
    newChildItems[index] = {
      ...newChildItems[index],
      [e.target.name]: e.target.value // Update specific field
    };
    onChange({
      target: {
        name: 'child',
        value: newChildItems // Update child array
      }
    });
  };

  // Handle deletion of a subitem
  const handleDeleteSubItem = (index) => {
    const updatedChildren = formData.child.filter((_, i) => i !== index); // Remove subitem at index
    onChange({
      target: {
        name: 'child',
        value: updatedChildren, // Update child array
      },
    });

    if (updatedChildren.length === 0) {
      setShowSubItemForm(false); // Hide subitem form if no subitems remain
    }
  };

  // Determine if validation error should be shown for a field
  const shouldShowError = (field) => {
    return validatedFields[field] && !formData[field]?.toString().trim(); // Show error if field is validated and empty
  };

  // Render nothing if modal is not shown
  if (!show) return null;

  // Render modal
  return (
    <div
      className={`modal fade ${show ? 'show d-block' : ''}`}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} // Modal backdrop
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          {/* Modal Header */}
          <div className="modal-header">
            <h5 className="modal-title">
              {isEditMode ? 'Edit Vehicle Checklist' : 'Add Vehicle Checklist'} {/* Dynamic title based on edit mode */}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose} // Close modal
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Name Input */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${shouldShowError('name') ? 'is-invalid' : ''}`} // Apply error styling
                      name="name"
                      value={formData.name || ''} // Checklist name value
                      onChange={onChange} // Handle input change
                      required // Mark as required
                    />
                    {shouldShowError('name') && (
                      <div className="invalid-feedback">Name is required</div> // Error message
                    )}
                  </div>
                </div>

                {/* Criteria Input */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>
                      Criteria <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-control ${shouldShowError('criteria') ? 'is-invalid' : ''}`} // Apply error styling
                      name="criteria"
                      value={formData.criteria || ''} // Criteria value
                      onChange={onChange} // Handle input change
                      required // Mark as required
                    />
                    {shouldShowError('criteria') && (
                      <div className="invalid-feedback">Criteria is required</div> // Error message
                    )}
                  </div>
                </div>
              </div>

              {/* SubItem Form */}
              {formData.child && formData.child.length > 0 && showSubItemForm &&
                formData.child.map((subItem, index) => (
                  <div key={index} className="row mt-3 border-top pt-3 align-items-center">
                    <h5 className="modal-title mb-4">
                      {subItem.name ? 'Edit Subitem' : 'Add Subitem'} {/* Dynamic subitem title */}
                    </h5>
                    <div className="col-md-5">
                      <div className="mb-3">
                        <label>
                          Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${validated && !subItem.name ? 'is-invalid' : ''}`} // Apply error styling
                          name="name"
                          value={subItem.name || ''} // Subitem name value
                          onChange={(e) => handleSubItemChange(index, e)} // Handle subitem change
                          required // Mark as required
                        />
                        {validated && !subItem.name && (
                          <div className="invalid-feedback">SubItem name is required</div> // Error message
                        )}
                      </div>
                    </div>
                    <div className="col-md-5">
                      <div className="mb-3">
                        <label>
                          Criteria <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          className={`form-control ${validated && !subItem.criteria ? 'is-invalid' : ''}`} // Apply error styling
                          name="criteria"
                          value={subItem.criteria || ''} // Subitem criteria value
                          onChange={(e) => handleSubItemChange(index, e)} // Handle subitem change
                          required // Mark as required
                        />
                        {validated && !subItem.criteria && (
                          <div className="invalid-feedback">SubItem criteria is required</div> // Error message
                        )}
                      </div>
                    </div>
                    <div className="col-md-2 d-flex align-items-center">
                      <button
                        type="button"
                        className="h4 border-0 bg-transparent"
                        onClick={() => handleDeleteSubItem(index)} // Delete subitem
                        title="Delete SubItem"
                      >
                        <i className="ti ti-trash text-danger"></i> {/* Trash icon */}
                      </button>
                    </div>
                  </div>
                ))}

              {/* Add SubItem Button */}
              {!formData.child?.length && !showSubItemForm && (
                <button
                  type="button"
                  className="btn btn-outline-primary mt-3"
                  onClick={handleAddSubItem} // Trigger adding subitem
                >
                  <i className="ti ti-circle-plus fs-4 me-2"></i> {/* Plus icon */}
                  Add SubItem
                </button>
              )}
            </form>
          </div>
          {/* Modal Footer */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn mb-1 waves-effect waves-light btn-outline-danger"
              onClick={onClose} // Close modal
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn mb-1 waves-effect waves-light btn-primary"
              onClick={handleSubmit} // Trigger form submission
            >
              {isEditMode ? 'Update' : 'Save'} Checklist {/* Dynamic button text based on edit mode */}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export the component
export default VehicleChecklistAddModal;